import { useEffect, useId, useMemo, useRef, useState } from "react";
import {
  Check,
  ImageIcon,
  Loader2,
  RefreshCw,
  Sparkles,
  Trash2,
  UserPlus,
  X,
} from "lucide-react";
import { Button, cn } from "@nexus/ui";
import {
  NEW_PARTITION_RULE,
  TenantOnboardingCreateResultSchema,
  type AddPreflightMarketInput,
  type OnboardingEditColumn,
} from "@nexus/contracts";
import { MarketMultiSelect } from "./MarketMultiSelect.js";
import { useTranslation } from "react-i18next";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  useProvisionTenant,
  useAllIndustries,
} from "@/features/identity-hub/hooks/useIdentityHub";
import {
  planNewOrgOnboarding,
  type NewOrgOnboarding,
  createOnboardingDraft,
  type OnboardingDraft,
  type OnboardingDraftValue,
} from "@/features/identity-hub/lib/client-onboarding";
import { toOnboardingSaveError, type OnboardingSaveError } from "@/features/identity-hub/lib/onboarding-save-error";
import { createCustomerSetupDraft, type CustomerSetupDraft } from "@/features/identity-hub/lib/customer-setup";
import {
  identityHubKeys,
  onboardingAvailabilityQueryOptions,
  tenantOnboardingKeys,
} from "@/lib/queries/identity-hub.queries";
import { brandBrainKeys } from "@/lib/queries/brand-brain.queries";
import { usePermissions, useIsSuperTenant } from "@/features/auth/hooks/usePermissions";
import { useAuth } from "@/features/auth/hooks/useAuth";
import {
  adminUpdateTenantSettings,
  awaitCustomerPartitionCheck,
  awaitNewOrgOnboarding,
  createTenantOnboarding,
  startCustomerPartitionCheck,
  type StatusResult,
} from "@/features/identity-hub/api/identity-hub.api";
import { selectAssignableIndustries } from "@/features/identity-hub/lib/assignable-industries";
import { BRAND_COLORS } from "@/features/identity-hub/lib/brand-colors";
import { TENANT_LOGO_ACCEPT } from "@/features/identity-hub/lib/tenant-logo-upload-constraints";
import { useTenantLogoUpload } from "@/features/identity-hub/hooks/useTenantLogoUpload";
import { orgColor, orgInitials } from "@/features/identity-hub/utils/iam.utils";
import { orgNameError } from "@/features/identity-hub/lib/role-field-errors";
import {
  DEFAULT_TENANT_PRODUCT_LINE,
  isNewOrgDraftDirty,
  type TenantProductLine,
} from "@/features/identity-hub/lib/org-settings-dirty";
import { OrgDrawerUnsavedChangesGuard } from "./shared/drawer/OrgDrawerUnsavedChangesGuard";
import { IAM_ORG_DRAWER_MIN_WIDTH_PX, IamOverlayDrawer } from "./shared/drawer/IamOverlayDrawer";
import { ORG_ACCENT_DRAWER_TOP_CLASS, orgAccentVar } from "@/features/identity-hub/utils/org-accent-surface";
import { OrgLogoMark } from "./OrgLogoMark";
import { AccentColorControl } from "./shared/AccentColorControl";
import {
  NewOrgDrawerBodyLayout,
  NewOrgReadyToCreateAside,
  NewOrgSectionHeading,
} from "./new-org/NewOrgCreateAside";
import { NewOrgIndustryRows } from "./new-org/NewOrgIndustryRows";
import {
  NEW_ORG_STEP_ORDER,
  NEXUS_ONLY_STEP_ORDER,
  NewOrgStepStrip,
  newOrgStepIndex,
  type NewOrgStep,
} from "./new-org/NewOrgStepStrip";
import { NewOrgOnboardingStep } from "./new-org/NewOrgOnboardingStep";
import {
  NewOrgReviewStep,
  type NewOrgExistingCustomer,
  type NewOrgPartitionCheck,
  type NewOrgReviewOnboarding,
} from "./new-org/NewOrgReviewStep";

interface PendingSettingsPayload {
  defaultIndustry?: string | null;
  defaultBrand?: string | null;
}

/** New Organization always creates an organization tenant; the type is not offered in this flow. */
const NEW_ORG_TENANT_TYPE = "ORGANIZATION";

const INPUT_CLASS =
  "h-9 rounded-md border border-border bg-background px-3 text-sm text-foreground outline-none placeholder:text-muted-foreground/60 focus:border-ring focus:ring-1 focus:ring-ring/40";

const PRODUCT_LINE_OPTIONS = [
  { value: "CORE", labelKey: "iam.newOrgDialog.productLineCore", labelDefault: "Core", hintKey: "iam.newOrgDialog.productLineCoreHint", hintDefault: "Own brand" },
  { value: "COMP", labelKey: "iam.newOrgDialog.productLineComp", labelDefault: "Comp", hintKey: "iam.newOrgDialog.productLineCompHint", hintDefault: "Competitors" },
  { value: "CORE_AND_COMP", labelKey: "iam.newOrgDialog.productLineCoreAndComp", labelDefault: "Core & Comp", hintKey: "iam.newOrgDialog.productLineCoreAndCompHint", hintDefault: "Both · default" },
] as const;

interface NewOrgDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  superTenantId: string | null;
  onInviteFirstAdmin?: () => void;
}

export function NewOrgDialog({
  open,
  onOpenChange,
  superTenantId,
  onInviteFirstAdmin,
}: NewOrgDialogProps) {
  const { t } = useTranslation();

  const [phase, setPhase] = useState<"wizard" | "success">("wizard");
  const [step, setStep] = useState<NewOrgStep>("basics");
  const [maxVisitedStep, setMaxVisitedStep] = useState<NewOrgStep>("basics");

  const [name, setName] = useState("");
  const [isNameTouched, setIsNameTouched] = useState(false);
  const nameErrorId = useId();
  const [productLine, setProductLine] = useState<TenantProductLine>(DEFAULT_TENANT_PRODUCT_LINE);
  const [industryIndices, setIndustryIndices] = useState<string[]>([]);
  const [preflightMarketRefs, setPreflightMarketRefs] = useState<AddPreflightMarketInput[]>([]);
  const [defaultIndustry, setDefaultIndustry] = useState<string | null>(null);
  const [defaultBrand, setDefaultBrand] = useState("");
  const [color, setColor] = useState<string | null>(BRAND_COLORS[0].value);
  const [logoFile, setLogoFile] = useState<{ name: string; sizeBytes: number } | null>(null);

  const [customerPartition, setCustomerPartition] = useState("");
  /** Opt-in: full onboarding runs only when the user checks it on the onboarding step. */
  const [isOnboardingIncluded, setIsOnboardingIncluded] = useState(false);
  const availabilityQuery = useQuery(onboardingAvailabilityQueryOptions());
  const canWriteOnboarding = availabilityQuery.data?.writesEnabled === true;
  const stepOrder = canWriteOnboarding ? NEW_ORG_STEP_ORDER : NEXUS_ONLY_STEP_ORDER;
  const isNexusOnly = !isOnboardingIncluded || !canWriteOnboarding;
  const [reviewRun, setReviewRun] = useState(0);
  const [clientIndustry, setClientIndustry] = useState("");
  const [onboardingDraft, setOnboardingDraft] = useState<OnboardingDraft>(createOnboardingDraft);
  const [setupDraft, setSetupDraft] = useState<CustomerSetupDraft>(createCustomerSetupDraft);

  const [createdTenantId, setCreatedTenantId] = useState<string | null>(null);
  /** One UUID for the provision request, the first publish, and every retry. */
  const onboardingIdempotencyKey = useRef<string | null>(null);
  // Set synchronously on the first click, so a second click before the re-render can't send another create.
  // Client-side only: it doesn't stop a concurrent create from another tab or a retry after a lost response.
  // The provision endpoint doesn't dedupe organizations by idempotency key yet (server-side follow-up).
  const isSubmitInFlight = useRef(false);
  const [compKey, setCompKey] = useState("");
  const [isCompTouched, setIsCompTouched] = useState(false);
  const [pendingSettingsPayload, setPendingSettingsPayload] = useState<PendingSettingsPayload | null>(null);
  const [settingsSaveFailed, setSettingsSaveFailed] = useState(false);
  const [settingsRetryPending, setSettingsRetryPending] = useState(false);
  const [isOnboardingSubmitted, setIsOnboardingSubmitted] = useState(false);
  const [isFinishingCreate, setIsFinishingCreate] = useState(false);
  const [onboardingPhase, setOnboardingPhase] = useState<"verifying" | "saved" | null>(null);
  const [onboardingSaveError, setOnboardingSaveError] = useState<OnboardingSaveError | null>(null);
  const [unsavedCloseGuardOpen, setUnsavedCloseGuardOpen] = useState(false);
  /** Bumped when a submit is blocked by missing or invalid onboarding fields. */
  const [blockedSubmitCount, setBlockedSubmitCount] = useState(0);
  const onboardingStepRef = useRef<HTMLDivElement>(null);

  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const activeTenantId = useAuth((s) => s.activeTenantId);
  const logoUpload = useTenantLogoUpload({
    tenantId: activeTenantId,
    onUploaded: (confirmed) => setLogoUrl(confirmed.publicUrl ?? null),
  });
  const { previewUrl: logoPreviewUrl, progress: logoUploadProgress, inputRef: logoInputRef } = logoUpload;

  const qc = useQueryClient();
  const provisionTenant = useProvisionTenant({ showSuccessToast: false });
  const { data: industries = [] } = useAllIndustries();
  const perms = usePermissions();
  const isSuperTenant = useIsSuperTenant();
  const hasPermission = perms.IDENTITY_HUB_CREATE_TENANT.isOn && isSuperTenant;
  const isLogoUploading = logoUpload.isUploading;
  const hasLogo = Boolean(logoPreviewUrl || logoUrl);

  const nameTrimmed = name.trim();
  const previewAccent = color || orgColor(nameTrimmed || "Org");
  const nameError = orgNameError(t, name);
  const showNameError = nameError !== null && (isNameTouched || name.length > 0);
  const effectiveCompKey = isCompTouched ? compKey : compKey || customerPartition;
  const onboardingPlan = useMemo(
    () =>
      planNewOrgOnboarding({
        customerPartition,
        clientIndustry,
        draft: onboardingDraft,
        setupDraft,
        idempotencyKey: onboardingIdempotencyKey.current ?? "",
        compKey: effectiveCompKey,
      }),
    [customerPartition, clientIndustry, onboardingDraft, setupDraft, effectiveCompKey],
  );

  const canContinue =
    !nameError &&
    !!superTenantId &&
    hasPermission &&
    !provisionTenant.isPending &&
    !isLogoUploading;

  const productLineLabel = useMemo(() => {
    const match = PRODUCT_LINE_OPTIONS.find((o) => o.value === productLine);
    return match ? t(match.labelKey, match.labelDefault) : productLine;
  }, [productLine, t]);

  const assignableIndustryCount = useMemo(
    () => selectAssignableIndustries(industries).length,
    [industries],
  );

  const industriesSummary =
    industryIndices.length === 0
      ? t("iam.newOrgDialog.aside.reviewNone", "None")
      : t("iam.newOrgDialog.aside.reviewIndustriesCount", "{{count}} selected", {
          count: industryIndices.length,
        });

  const marketsSummary =
    preflightMarketRefs.length === 0
      ? t("iam.newOrgDialog.aside.reviewGlobalOnly", "Global only")
      : t("iam.newOrgDialog.aside.reviewGlobalPlus", "Global + {{count}}", {
          count: preflightMarketRefs.length,
        });

  const reset = () => {
    setPhase("wizard");
    setStep("basics");
    setMaxVisitedStep("basics");
    setName("");
    setIsNameTouched(false);
    setProductLine(DEFAULT_TENANT_PRODUCT_LINE);
    setIndustryIndices([]);
    setPreflightMarketRefs([]);
    setDefaultIndustry(null);
    setDefaultBrand("");
    setColor(BRAND_COLORS[0].value);
    setLogoUrl(null);
    setLogoFile(null);
    logoUpload.clearPreview();
    setCustomerPartition("");
    setIsOnboardingIncluded(false);
    setCompKey("");
    setIsCompTouched(false);
    setClientIndustry("");
    setOnboardingDraft(createOnboardingDraft());
    setSetupDraft(createCustomerSetupDraft());
    setCreatedTenantId(null);
    onboardingIdempotencyKey.current = null;
    setPendingSettingsPayload(null);
    setSettingsSaveFailed(false);
    setSettingsRetryPending(false);
    setIsOnboardingSubmitted(false);
    setIsFinishingCreate(false);
    setOnboardingSaveError(null);
  };

  const isDraftDirty = isNewOrgDraftDirty({
    phase,
    name,
    productLine,
    industryIndices,
    preflightMarketCount: preflightMarketRefs.length,
    defaultIndustry,
    defaultBrand,
    hasLogo,
    brandColor: color,
    defaultBrandColor: BRAND_COLORS[0].value,
    visitedBeyondBasics: maxVisitedStep !== "basics",
  });

  const closeDrawer = () => {
    setUnsavedCloseGuardOpen(false);
    reset();
    onOpenChange(false);
  };

  const handleOpenChange = (v: boolean) => {
    if (v) {
      onOpenChange(true);
      return;
    }
    if (isDraftDirty) {
      setUnsavedCloseGuardOpen(true);
      return;
    }
    closeDrawer();
  };

  const visitStep = (next: NewOrgStep) => {
    setStep(next);
    if (newOrgStepIndex(next, stepOrder) > newOrgStepIndex(maxVisitedStep, stepOrder)) {
      setMaxVisitedStep(next);
    }
  };

  const handleLogoRemove = () => {
    logoUpload.clearPreview();
    setLogoUrl(null);
    setLogoFile(null);
  };

  const buildSettingsPayload = () => {
    const effectiveDefault = industryIndices.includes(defaultIndustry ?? "")
      ? defaultIndustry
      : industryIndices.length === 1
        ? industryIndices[0]!
        : null;
    const trimmedBrand = defaultBrand.trim();
    const settingsPayload: PendingSettingsPayload = {};
    if (effectiveDefault) settingsPayload.defaultIndustry = effectiveDefault;
    if (trimmedBrand) settingsPayload.defaultBrand = trimmedBrand;
    return settingsPayload;
  };

  const noteOnboardingStatus = (status: StatusResult) => {
    if (status.status === "COMPLETED") setOnboardingPhase("saved");
    else if (status.status === "FAILED") setOnboardingPhase(null);
    else if (status.status === "ACCEPTED") setOnboardingPhase(status.phase === "verifying" ? "verifying" : null);
  };

  /** True once the client-context API accepted the create and Nexus saved it; otherwise the failure is kept for the onboarding step. */
  const runOnboardingCreate = async (run: () => Promise<StatusResult>): Promise<boolean> => {
    const fallbackMessage = t("iam.newOrgDialog.onboarding.notSaved", {
      name: nameTrimmed,
      defaultValue: "{{name}} was created, but its customer data could not be saved. Please try again.",
    });
    try {
      const status = await run();
      if (!TenantOnboardingCreateResultSchema.safeParse(status.result).success) {
        setOnboardingSaveError({ message: fallbackMessage, isGeneral: true });
        return false;
      }
      void qc.invalidateQueries({ queryKey: tenantOnboardingKeys.all });
      void qc.invalidateQueries({ queryKey: brandBrainKeys.all });
      return true;
    } catch (err) {
      setOnboardingSaveError(toOnboardingSaveError(err, fallbackMessage, t));
      return false;
    }
  };

  const finishOnboardingCreate = (isSaved: boolean) => {
    if (!isSaved) {
      visitStep("onboarding");
      return;
    }
    toast.success(t("iam.toast.organizationCreated"));
    setPhase("success");
  };

  /** The organization (and its customer partition) already exists; only the onboarding create is sent again. */
  const resubmitOnboarding = async (tenantId: string, onboarding: NewOrgOnboarding) => {
    if (isSubmitInFlight.current) return;
    isSubmitInFlight.current = true;
    setOnboardingPhase(null);
    setIsFinishingCreate(true);
    try {
      const isSaved = await runOnboardingCreate(async () => {
        onboardingIdempotencyKey.current ??= crypto.randomUUID();
        const { correlationId } = await createTenantOnboarding({
          tenantId,
          fields: onboarding.fields,
          setup: onboarding.setup,
          compKey: onboarding.compKey,
          idempotencyKey: onboardingIdempotencyKey.current,
        });
        return awaitNewOrgOnboarding(correlationId, noteOnboardingStatus);
      });
      finishOnboardingCreate(isSaved);
    } finally {
      isSubmitInFlight.current = false;
      setIsFinishingCreate(false);
    }
  };

  const applyTenantSettings = async (tenantId: string, settingsPayload: PendingSettingsPayload) => {
    if (Object.keys(settingsPayload).length === 0) return true;
    await adminUpdateTenantSettings(tenantId, settingsPayload);
    void qc.invalidateQueries({ queryKey: identityHubKeys.all });
    return true;
  };

  // Read-only pre-create check, started when the review opens. Nexus usage answers at once;
  // the existing-customer read (BigQuery and the Nexus onboarding copy) is a data-sync job we poll.
  const partitionForCheck = customerPartition.trim();
  const isCheckEnabled = step === "review" && partitionForCheck.length > 0;
  const usageQuery = useQuery({
    queryKey: identityHubKeys.customerPartitionCheck(partitionForCheck, reviewRun),
    queryFn: () => startCustomerPartitionCheck(partitionForCheck),
    enabled: isCheckEnabled,
    staleTime: Infinity,
    retry: false,
  });
  const checkCorrelationId = usageQuery.data?.correlationId;
  const existingQuery = useQuery({
    queryKey: identityHubKeys.customerPartitionExisting(checkCorrelationId ?? ""),
    queryFn: () => awaitCustomerPartitionCheck(checkCorrelationId as string),
    enabled: isCheckEnabled && Boolean(checkCorrelationId),
    staleTime: Infinity,
    retry: false,
  });
  const existingCustomerState: NewOrgExistingCustomer = !checkCorrelationId
    ? { status: "unverified" }
    : existingQuery.data === undefined
      ? { status: "pending" }
      : existingQuery.data === null
        ? { status: "unverified" }
        : { status: "found", result: existingQuery.data };
  const partitionCheck: NewOrgPartitionCheck = !partitionForCheck
    ? { status: "none" }
    : usageQuery.isError
      ? { status: "error" }
      : !usageQuery.data
        ? { status: "loading" }
        : { status: "ready", usage: usageQuery.data.usage, existing: existingCustomerState };
  const isPartitionUsed = partitionCheck.status === "ready" && partitionCheck.usage.usedBy.length > 0;
  const isNewCustomerVerified =
    partitionCheck.status === "ready" &&
    partitionCheck.existing.status === "found" &&
    !partitionCheck.existing.result.existsInBigQuery &&
    !partitionCheck.existing.result.existsInNexusOnboarding &&
    !isPartitionUsed;
  // Full onboarding needs a completed check that found no existing customer. Nexus only writes nothing to
  // BigQuery, so it never waits for that check; it only needs the immediate Nexus answer.
  const canConfirmCreate = isNexusOnly
    ? !isPartitionUsed && partitionCheck.status !== "loading"
    : isNewCustomerVerified;

  // A blocked submit moves focus to the first invalid field; collapsed sections holding an error are already open.
  useEffect(() => {
    if (blockedSubmitCount === 0) return;
    const step = onboardingStepRef.current;
    const invalid = step?.querySelector<HTMLElement>('[aria-invalid="true"]');
    if (invalid) {
      invalid.focus();
      return;
    }
    step?.querySelector<HTMLElement>('[role="alert"]')?.scrollIntoView?.({ block: "center" });
  }, [blockedSubmitCount]);

  /** Review needs a complete onboarding when it is included; otherwise the onboarding step shows what is missing. */
  const handleReview = () => {
    if (!isNexusOnly && !onboardingPlan.onboarding) {
      setIsOnboardingSubmitted(true);
      visitStep("onboarding");
      setBlockedSubmitCount((count) => count + 1);
      return;
    }
    setReviewRun((run) => run + 1);
    visitStep("review");
  };

  /** An existing or unverifiable customer: create only the Nexus organization and keep the entered partition. */
  const handleSwitchToNexusOnly = () => setIsOnboardingIncluded(false);

  /** Runs only from the review step's confirm; nothing is persisted before it. */
  const handleCreate = async () => {
    if (!superTenantId || !hasPermission || !canConfirmCreate) return;
    const planned = isNexusOnly ? null : onboardingPlan.onboarding;
    if (!isNexusOnly && !planned) {
      handleReview();
      return;
    }
    if (isSubmitInFlight.current) return;
    isSubmitInFlight.current = true;
    setOnboardingSaveError(null);
    setOnboardingPhase(null);
    onboardingIdempotencyKey.current ??= crypto.randomUUID();
    const onboarding = planned ? { ...planned, idempotencyKey: onboardingIdempotencyKey.current } : null;
    const localPartition = customerPartition.trim();

    try {
      const result = await provisionTenant.mutateAsync({
        name: nameTrimmed,
        type: NEW_ORG_TENANT_TYPE,
        tenantId: superTenantId,
        copyAccessFromTenantId: superTenantId,
        productLine,
        brandColor: color,
        ...(logoUrl ? { logoUrl } : {}),
        ...(industryIndices.length > 0 ? { industryIndices } : {}),
        ...(preflightMarketRefs.length > 0 ? { preflightMarketRefs } : {}),
        ...(onboarding ? { onboarding } : localPartition ? { customerPartition: localPartition } : {}),
        idempotencyKey: onboardingIdempotencyKey.current,
      });

      setIsFinishingCreate(true);
      const tenantId = result.tenantId as string | undefined;
      const onboardingCorrelationId = result.onboardingCorrelationId as string | undefined;
      const settingsPayload = buildSettingsPayload();
      setCreatedTenantId(tenantId ?? null);
      setPendingSettingsPayload(Object.keys(settingsPayload).length > 0 ? settingsPayload : null);
      setSettingsSaveFailed(false);

      if (tenantId && Object.keys(settingsPayload).length > 0) {
        try {
          await applyTenantSettings(tenantId, settingsPayload);
        } catch {
          setSettingsSaveFailed(true);
        }
      }

      if (!onboarding) {
        finishOnboardingCreate(true);
        return;
      }
      const isSaved = await runOnboardingCreate(() =>
        onboardingCorrelationId
          ? awaitNewOrgOnboarding(onboardingCorrelationId, noteOnboardingStatus)
          : Promise.reject(new Error("Client onboarding was not started")),
      );
      finishOnboardingCreate(isSaved);
    } catch {
      // onError toast shown by useProvisionTenant
    } finally {
      isSubmitInFlight.current = false;
      setIsFinishingCreate(false);
    }
  };

  /** The organization exists but its onboarding failed; only the onboarding create is sent again. */
  const handleResubmitOnboarding = async () => {
    if (!createdTenantId) return;
    const planned = onboardingPlan.onboarding;
    if (!planned) {
      setIsOnboardingSubmitted(true);
      setBlockedSubmitCount((count) => count + 1);
      return;
    }
    setOnboardingSaveError(null);
    onboardingIdempotencyKey.current ??= crypto.randomUUID();
    await resubmitOnboarding(createdTenantId, { ...planned, idempotencyKey: onboardingIdempotencyKey.current });
  };

  const handleRetrySettings = async () => {
    if (!createdTenantId || !pendingSettingsPayload) return;
    setSettingsRetryPending(true);
    try {
      await applyTenantSettings(createdTenantId, pendingSettingsPayload);
      setSettingsSaveFailed(false);
    } catch {
      setSettingsSaveFailed(true);
    } finally {
      setSettingsRetryPending(false);
    }
  };

  const handleOnboardingFieldChange = (column: OnboardingEditColumn, value: OnboardingDraftValue) => {
    setOnboardingDraft((current) => ({ ...current, [column]: value }));
  };

  const isPending = provisionTenant.isPending || isFinishingCreate;
  const stepIndex = newOrgStepIndex(step, stepOrder);
  const nextStep = stepOrder[stepIndex + 1];
  const previousStep = stepOrder[stepIndex - 1];

  const footerContinueLabel =
    nextStep === "dataAccess"
      ? t("iam.newOrgDialog.continueDataAccess", "Continue to data access")
      : nextStep === "onboarding"
        ? t("iam.newOrgDialog.continueOnboarding", "Continue to customer data")
        : t("iam.newOrgDialog.continueReview", "Review");

  const industryTitleByIndex = new Map(industries.map((industry) => [industry.index, industry.title]));
  const effectiveDefaultIndustry = buildSettingsPayload().defaultIndustry ?? null;
  const plannedOnboarding = onboardingPlan.onboarding;
  const reviewOnboarding: NewOrgReviewOnboarding | null = isNexusOnly
    ? { mode: "skip", customerPartition: customerPartition.trim() || null, isOnboardingAvailable: canWriteOnboarding }
    : plannedOnboarding
      ? {
          mode: "create",
          customerPartition: plannedOnboarding.customerPartition,
          compKey: plannedOnboarding.compKey,
          clientIndustry: clientIndustry.trim(),
          metaAccountIds: plannedOnboarding.setup.account_id ?? [],
          tsStart: `${String(plannedOnboarding.setup.ts_start.month).padStart(2, "0")}/${plannedOnboarding.setup.ts_start.year}`,
          taggingSector: plannedOnboarding.setup.tagging_sector,
          filledFieldCount: Object.keys(plannedOnboarding.fields).filter((column) => column !== "industry").length,
        }
      : null;

  const onboardingPendingLabel =
    onboardingPhase === "verifying"
      ? t("iam.newOrgDialog.verifying", "Verifying…")
      : onboardingPhase === "saved"
        ? t("iam.newOrgDialog.saved", "Saved")
        : t("iam.newOrgDialog.creating");

  const aside =
    step === "onboarding" ? (
      <NewOrgReadyToCreateAside
        name={nameTrimmed || t("iam.newOrgDialog.untitledOrg", "Untitled organization")}
        productLineLabel={productLineLabel}
        industriesSummary={industriesSummary}
        marketsSummary={marketsSummary}
        defaultBrand={defaultBrand.trim() || t("iam.shared.none", "None")}
        accentLabel={color ?? t("iam.accentColor.sourceAutoLong", "Auto from name")}
      />
    ) : null;

  return (
    <IamOverlayDrawer
      open={open}
      onOpenChange={handleOpenChange}
      title={t("iam.newOrgDialog.title")}
      description={t("iam.newOrgDialog.description")}
      className={ORG_ACCENT_DRAWER_TOP_CLASS}
      style={orgAccentVar(previewAccent)}
      minWidthPx={IAM_ORG_DRAWER_MIN_WIDTH_PX}
    >
      <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden">
        {/* Live preview header */}
        <div className="flex shrink-0 items-start gap-3 border-b border-border-subtle bg-[linear-gradient(145deg,color-mix(in_srgb,var(--org-accent)_14%,var(--card))_0%,color-mix(in_srgb,var(--org-accent)_5%,var(--card))_50%,var(--card)_100%)] px-5 py-4">
          {logoPreviewUrl || logoUrl ? (
            <img
              src={logoPreviewUrl ?? logoUrl ?? undefined}
              alt={t("iam.newOrgDialog.logoPreviewAlt")}
              className="size-11 shrink-0 overflow-hidden rounded-lg border border-border/60 bg-background object-cover"
            />
          ) : phase === "success" && createdTenantId ? (
            <OrgLogoMark
              name={nameTrimmed}
              logoUrl={logoUrl}
              accent={previewAccent}
              size={44}
            />
          ) : (
            <div
              className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg border text-[15px] font-semibold text-white"
              style={{
                backgroundColor: previewAccent,
                borderColor: `color-mix(in srgb, ${previewAccent} 70%, white)`,
              }}
            >
              {nameTrimmed ? orgInitials(nameTrimmed) : "…"}
            </div>
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-mono text-[10px] uppercase tracking-[0.04em] text-muted-foreground">
                {t("iam.newOrgDialog.title")}
              </span>
              {phase === "wizard" && (
                <span className="inline-flex items-center rounded-full border border-warning-border bg-warning-subtle px-1.5 py-[3px] text-[10px] font-medium leading-none text-warning-subtle-foreground">
                  {t("iam.newOrgDialog.draftBadge", "Draft · not saved")}
                </span>
              )}
              {phase === "success" && (
                <span className="rounded-full border border-success-subtle bg-success-subtle px-1.5 py-[3px] text-[10px] font-medium text-success-subtle-foreground">
                  {t("iam.newOrgDialog.createdBadge", "Created")}
                </span>
              )}
            </div>
            <h3 className="truncate text-[15px] font-semibold leading-tight">
              {nameTrimmed || t("iam.newOrgDialog.untitledOrg", "Untitled organization")}
            </h3>
            <p className="text-[11px] text-muted-foreground">
              {productLineLabel}
            </p>
          </div>
          <button
            type="button"
            onClick={() => handleOpenChange(false)}
            className="flex h-7 w-7 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label={t("iam.orgDrawer.closePanel", "Close panel")}
          >
            <X className="h-3.5 w-3.5" />
          </button>
        </div>

        {phase === "wizard" && (
          <>
            <NewOrgStepStrip
              steps={stepOrder}
              activeStep={step}
              maxVisitedStep={maxVisitedStep}
              onStepChange={(next) => (next === "review" ? handleReview() : visitStep(next))}
            />

            <NewOrgDrawerBodyLayout aside={aside}>
              {step === "basics" && (
                <div className="flex flex-col gap-7">
                  <section className="flex flex-col gap-4">
                    <NewOrgSectionHeading
                      title={t("iam.newOrgDialog.basicInfoTitle", "Basic information")}
                      hint={t(
                        "iam.newOrgDialog.identitySectionHint",
                        "The header above updates as you type — that’s how it will look in Identity Hub.",
                      )}
                    />
                  <div className="flex flex-col gap-1.5">
                    <div className="flex items-baseline justify-between gap-2">
                      <label className="text-xs font-medium text-foreground">
                        {t("iam.newOrgDialog.displayNameLabel")}{" "}
                        <span className="text-destructive">*</span>
                      </label>
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {nameTrimmed.length} / 255
                      </span>
                    </div>
                    <input
                      type="text"
                      placeholder={t("iam.newOrgDialog.displayNamePlaceholder")}
                      value={name}
                      onChange={(e) => setName(e.target.value)}
                      onBlur={() => setIsNameTouched(true)}
                      aria-label={t("iam.newOrgDialog.displayNameLabel")}
                      aria-invalid={showNameError || undefined}
                      aria-describedby={showNameError ? nameErrorId : undefined}
                      required
                      className={cn(INPUT_CLASS, showNameError && "border-destructive focus:border-destructive")}
                      autoFocus
                    />
                    {showNameError && <p id={nameErrorId} className="text-[11px] text-destructive">{nameError}</p>}
                  </div>
                  </section>

                  <section className="flex flex-col gap-2">
                    <div className="flex flex-col gap-0.5">
                      <span className="text-xs font-medium text-foreground">
                        {t("iam.newOrgDialog.productLineLabel", "Product line")}
                      </span>
                      <span className="text-[11px] leading-[1.45] text-muted-foreground">
                        {t(
                          "iam.newOrgDialog.productLineHint",
                          "Controls which KPIs and menus the tenant sees.",
                        )}
                      </span>
                    </div>
                    <div
                      role="radiogroup"
                      aria-label={t("iam.newOrgDialog.productLineLabel", "Product line")}
                      className="grid grid-cols-3 gap-1 rounded-md border border-border bg-muted p-1"
                      data-testid="new-org-product-line-group"
                    >
                      {PRODUCT_LINE_OPTIONS.map((option) => {
                        const selected = productLine === option.value;
                        return (
                          <button
                            key={option.value}
                            type="button"
                            role="radio"
                            aria-checked={selected}
                            data-testid={`new-org-product-line-${option.value}`}
                            onClick={() => setProductLine(option.value as TenantProductLine)}
                            className={cn(
                              "flex flex-col items-center gap-0.5 rounded-sm p-2 text-center transition-colors",
                              selected
                                ? "border border-border bg-background shadow-sm"
                                : "border border-transparent bg-transparent hover:bg-background/50",
                            )}
                          >
                            <span
                              className={cn(
                                "text-xs font-medium",
                                selected ? "text-primary" : "text-foreground",
                              )}
                            >
                              {t(option.labelKey, option.labelDefault)}
                            </span>
                            <span className="text-[11px] leading-[1.45] text-muted-foreground">
                              {t(option.hintKey, option.hintDefault)}
                            </span>
                          </button>
                        );
                      })}
                    </div>
                  </section>

                  <section className="flex flex-col gap-2">
                    <NewOrgSectionHeading
                      title={t("iam.newOrgDialog.logoLabel")}
                      hint={t(
                        "iam.newOrgDialog.logoSectionHint",
                        "Shown in the header, org cards and the tenant switcher.",
                      )}
                    />
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept={TENANT_LOGO_ACCEPT}
                      className="sr-only"
                      disabled={isLogoUploading}
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) setLogoFile({ name: file.name, sizeBytes: file.size });
                        void logoUpload.handleFileChange(file);
                      }}
                    />
                    <div className="flex items-center gap-3 rounded-lg border border-border bg-background p-3">
                      <div className="relative shrink-0">
                        {logoPreviewUrl ? (
                          <img
                            src={logoPreviewUrl}
                            alt={t("iam.newOrgDialog.logoPreviewAlt")}
                            className="size-12 overflow-hidden rounded-md border border-border/60 bg-background object-cover"
                          />
                        ) : (
                          <div className="flex size-12 items-center justify-center rounded-md bg-muted text-muted-foreground">
                            <ImageIcon className="size-5 opacity-40" />
                          </div>
                        )}
                        {isLogoUploading && (
                          <div className="absolute inset-0 flex items-center justify-center rounded-md bg-background/80">
                            <Loader2 className="size-4 animate-spin text-primary" />
                          </div>
                        )}
                      </div>
                      <div className="flex min-w-0 flex-1 flex-col gap-0.5">
                        {hasLogo ? (
                          <>
                            <p className="truncate text-xs font-medium text-foreground">
                              {logoFile?.name ?? t("iam.newOrgDialog.logoFileFallback", "Logo")}
                            </p>
                            {isLogoUploading ? (
                              <div className="mt-1 h-1 w-full max-w-[200px] overflow-hidden rounded-full bg-muted">
                                <div
                                  className="h-full rounded-full bg-primary transition-all duration-200"
                                  style={{ width: `${logoUploadProgress ?? 0}%` }}
                                />
                              </div>
                            ) : (
                              <p className="flex items-center gap-1 text-[11px] leading-snug text-success-subtle-foreground">
                                <Check className="size-3 shrink-0" />
                                {logoFile
                                  ? t("iam.newOrgDialog.logoUploadedWithSize", "Uploaded · {{size}} KB", {
                                      size: Math.max(1, Math.round(logoFile.sizeBytes / 1024)),
                                    })
                                  : t("iam.newOrgDialog.logoUploaded", "Uploaded")}
                              </p>
                            )}
                          </>
                        ) : (
                          <p className="text-[11px] text-muted-foreground">
                            {t("iam.orgDrawer.settings.logoHint")}
                          </p>
                        )}
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="h-7 gap-1.5 px-2.5 text-[11px]"
                        iconStart={hasLogo ? "refresh-cw" : "upload"}
                        disabled={isLogoUploading}
                        onClick={() => logoInputRef.current?.click()}
                      >
                        {isLogoUploading
                          ? t("iam.orgDrawer.settings.logoUploading")
                          : hasLogo
                            ? t("iam.newOrgDialog.logoReplace", "Replace")
                            : t("iam.orgDrawer.settings.logoUpload")}
                      </Button>
                      {hasLogo && !isLogoUploading ? (
                        <button
                          type="button"
                          aria-label={t("iam.orgDrawer.settings.logoRemove")}
                          onClick={handleLogoRemove}
                          className="flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground hover:bg-muted hover:text-foreground"
                        >
                          <Trash2 className="size-3.5" />
                        </button>
                      ) : null}
                    </div>
                  </section>

                  <AccentColorControl
                    layout="stacked"
                    showSectionHeading
                    orgName={nameTrimmed || t("iam.newOrgDialog.untitledOrg", "Untitled organization")}
                    brandColor={color}
                    onBrandColorChange={setColor}
                    logoUrl={logoPreviewUrl ?? logoUrl}
                    logoFileName={logoFile?.name}
                  />
                </div>
              )}

              {step === "dataAccess" && (
                <div className="flex flex-col gap-7">
                  <section className="flex flex-col gap-3">
                    <NewOrgSectionHeading
                      title={t("iam.newOrgDialog.industriesLabel")}
                      hint={t(
                        "iam.newOrgDialog.industriesSectionHint",
                        "Which industry data they can explore. The starred one is pre-selected in Create Opportunity Set.",
                      )}
                      trailing={
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {t("iam.newOrgDialog.industriesCount", "{{count}} of {{total}}", {
                            count: industryIndices.length,
                            total: assignableIndustryCount,
                          })}
                        </span>
                      }
                    />
                    <NewOrgIndustryRows
                      industries={industries}
                      selected={industryIndices}
                      defaultIndex={
                        defaultIndustry && industryIndices.includes(defaultIndustry)
                          ? defaultIndustry
                          : (industryIndices[0] ?? null)
                      }
                      onSelectedChange={(next) => {
                        setIndustryIndices(next);
                        setDefaultIndustry((current) =>
                          current && next.includes(current) ? current : (next[0] ?? null),
                        );
                      }}
                      onDefaultChange={setDefaultIndustry}
                    />
                  </section>

                  <section className="flex flex-col gap-3">
                    <NewOrgSectionHeading
                      title={t("iam.newOrgDialog.marketsLabel")}
                      hint={t(
                        "iam.newOrgDialog.marketsSectionHint",
                        "Each Pre Flight report is filed under one market. Global is always available.",
                      )}
                      trailing={
                        <span className="shrink-0 text-[11px] text-muted-foreground">
                          {marketsSummary}
                        </span>
                      }
                    />
                    <MarketMultiSelect
                      layout="chips"
                      selected={preflightMarketRefs}
                      onChange={setPreflightMarketRefs}
                      placeholder={t("iam.newOrgDialog.marketsSelectPlaceholder", "Select markets…")}
                      ariaLabel={t("iam.newOrgDialog.marketsLabel")}
                    />
                  </section>

                  <section className="flex flex-col gap-1.5">
                    <label className="text-xs font-medium text-foreground">
                      {t("iam.newOrgDialog.defaultBrandLabel", "Default brand")}
                      <span className="ml-1 font-normal text-muted-foreground">
                        {t("iam.newOrgDialog.defaultBrandOptional", "(optional)")}
                      </span>
                    </label>
                    <input
                      type="text"
                      placeholder={t("iam.newOrgDialog.defaultBrandPlaceholder", "e.g. Vodafone")}
                      value={defaultBrand}
                      onChange={(e) => setDefaultBrand(e.target.value)}
                      className={cn(INPUT_CLASS, "max-w-[360px] text-[13px]")}
                    />
                    {nameTrimmed ? (
                      <p className="flex items-center gap-1.5 text-[11px] leading-snug text-success-subtle-foreground">
                        <Sparkles className="size-3 shrink-0" />
                        {t(
                          "iam.newOrgDialog.defaultBrandSuggestion",
                          "Suggested from the display name · My Brand in Explore and Opportunities",
                        )}
                      </p>
                    ) : null}
                  </section>
                </div>
              )}

              {step === "onboarding" && (
                <div ref={onboardingStepRef}>
                  <NewOrgOnboardingStep
                    customerPartition={customerPartition}
                    onCustomerPartitionChange={setCustomerPartition}
                    compKey={effectiveCompKey}
                    onCompKeyChange={(value) => {
                      setIsCompTouched(true);
                      setCompKey(value);
                    }}
                    compError={
                      isOnboardingSubmitted && onboardingPlan.issue === "compRequired"
                        ? t("iam.newOrgDialog.onboarding.compRequired", "Competitor partition is required")
                        : isOnboardingSubmitted && onboardingPlan.issue === "compInvalid"
                          ? t("iam.onboarding.errors.partitionInvalid", NEW_PARTITION_RULE)
                          : onboardingSaveError?.field === "compKey"
                            ? onboardingSaveError.message
                            : null
                    }
                    clientIndustry={clientIndustry}
                    onClientIndustryChange={setClientIndustry}
                    draft={onboardingDraft}
                    onDraftChange={handleOnboardingFieldChange}
                    errors={onboardingPlan.errors}
                    setupDraft={setupDraft}
                    onSetupDraftChange={setSetupDraft}
                    setupErrors={onboardingPlan.setupErrors}
                    issue={isNexusOnly ? null : onboardingPlan.issue}
                    isIncluded={isOnboardingIncluded}
                    onIncludedChange={setIsOnboardingIncluded}
                    showErrors={isOnboardingSubmitted}
                    saveError={onboardingSaveError}
                    createdOrganizationName={createdTenantId ? nameTrimmed : null}
                  />
                </div>
              )}

              {step === "review" &&
                (reviewOnboarding ? (
                  <NewOrgReviewStep
                    organization={{
                      name: nameTrimmed,
                      logoLabel: hasLogo ? logoFile?.name ?? t("iam.newOrgDialog.logoUploaded", "Uploaded") : t("iam.shared.none", "None"),
                      accentLabel: color ?? t("iam.accentColor.sourceAutoLong", "Auto from name"),
                      productLineLabel,
                      industries: industryIndices.map((index) => industryTitleByIndex.get(index) ?? index),
                      defaultIndustry: effectiveDefaultIndustry
                        ? industryTitleByIndex.get(effectiveDefaultIndustry) ?? effectiveDefaultIndustry
                        : null,
                      marketsSummary,
                      defaultBrand: defaultBrand.trim() || null,
                    }}
                    onboarding={reviewOnboarding}
                    partitionCheck={partitionCheck}
                    onSwitchToNexusOnly={handleSwitchToNexusOnly}
                  />
                ) : (
                  <p className="text-xs text-muted-foreground">
                    {t("iam.newOrgDialog.review.finishOnboarding", "Finish setting up customer data, or turn it off, before reviewing.")}
                  </p>
                ))}
            </NewOrgDrawerBodyLayout>

            <div className="flex shrink-0 items-center gap-4 border-t border-border px-5 py-3">
              <div className="flex flex-1 items-center gap-3">
                <div className="flex w-24 shrink-0 gap-1">
                  {stepOrder.map((stepId, i) => (
                    <div
                      key={stepId}
                      className={cn(
                        "h-1 flex-1 rounded-full",
                        i <= stepIndex ? "bg-primary" : "bg-border",
                      )}
                    />
                  ))}
                </div>
                <span className="text-xs text-muted-foreground">
                  {t("iam.newOrgDialog.stepCount", "Step {{current}} of {{total}}", {
                    current: stepIndex + 1,
                    total: stepOrder.length,
                  })}
                </span>
              </div>
              <div className="flex shrink-0 items-center gap-2">
                {step === "review" ? (
                  <Button
                    variant="outline"
                    size="sm"
                    iconStart="arrow-left"
                    disabled={isPending}
                    onClick={() => previousStep && visitStep(previousStep)}
                    data-testid="new-org-review-back"
                  >
                    {t("iam.newOrgDialog.review.back", "Back to edit")}
                  </Button>
                ) : !previousStep ? (
                  <Button variant="ghost" size="sm" onClick={() => handleOpenChange(false)}>
                    {t("common.cancel")}
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    size="sm"
                    iconStart="arrow-left"
                    onClick={() => visitStep(previousStep)}
                  >
                    {t("iam.newOrgDialog.back", "Back")}
                  </Button>
                )}
                {(step === "basics" || step === "dataAccess") && nextStep !== "review" && (
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={step === "basics" || !canContinue}
                    onClick={handleReview}
                  >
                    {t("iam.newOrgDialog.reviewNow", "Review and create")}
                  </Button>
                )}
                {(step === "basics" || step === "dataAccess") && nextStep ? (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    iconEnd="arrow-right"
                    disabled={!canContinue}
                    onClick={() => (nextStep === "review" ? handleReview() : visitStep(nextStep))}
                  >
                    {footerContinueLabel}
                  </Button>
                ) : step === "onboarding" && createdTenantId ? (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    iconStart="check"
                    disabled={!canContinue || isPending}
                    onClick={() => void handleResubmitOnboarding()}
                  >
                    {isPending ? onboardingPendingLabel : t("iam.newOrgDialog.onboarding.resubmit", "Save customer data")}
                  </Button>
                ) : step === "onboarding" ? (
                  <Button size="sm" className="gap-1.5" iconEnd="arrow-right" disabled={!canContinue} onClick={handleReview}>
                    {t("iam.newOrgDialog.continueReview", "Review")}
                  </Button>
                ) : (
                  <Button
                    size="sm"
                    className="gap-1.5"
                    iconStart="check"
                    disabled={!canContinue || isPending || !canConfirmCreate || !reviewOnboarding}
                    onClick={() => void handleCreate()}
                    data-testid="new-org-confirm-create"
                  >
                    {isPending ? onboardingPendingLabel : t("iam.newOrgDialog.review.confirm", "Confirm and create")}
                  </Button>
                )}
              </div>
            </div>
          </>
        )}

        {phase === "success" && (
          <div className="flex min-h-0 flex-1 flex-col items-center justify-center gap-6 px-8 py-10 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-full bg-success-subtle">
              <Check className="h-6 w-6 text-success-subtle-foreground" />
            </div>
            <div className="max-w-md space-y-2">
              <h3 className="text-lg font-semibold text-foreground">
                {t("iam.newOrgDialog.successTitle", "{{name}} is ready", { name: nameTrimmed })}
              </h3>
              <p className="text-sm text-muted-foreground">
                {t(
                  "iam.newOrgDialog.successDescription",
                  "The organization was created. Invite its first admin to get them signed in.",
                )}
              </p>
            </div>
            {settingsSaveFailed && pendingSettingsPayload && (
              <div className="flex max-w-md flex-col items-center gap-3 rounded-lg border border-warning-border bg-warning-subtle px-4 py-3 text-left">
                <p className="text-sm text-warning-subtle-foreground">
                  {t(
                    "iam.newOrgDialog.settingsPartialFailure",
                    "The organization was created, but default industry/brand settings could not be saved.",
                  )}
                </p>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  disabled={settingsRetryPending}
                  onClick={() => void handleRetrySettings()}
                >
                  {settingsRetryPending ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" />
                  )}
                  {t("iam.newOrgDialog.settingsRetry", "Retry saving settings")}
                </Button>
              </div>
            )}
            <div className="flex flex-wrap items-center justify-center gap-2">
              <Button variant="outline" size="sm" onClick={() => handleOpenChange(false)}>
                {t("iam.newOrgDialog.done", "Done")}
              </Button>
              {onInviteFirstAdmin && (
                <Button
                  size="sm"
                  className="gap-1.5"
                  onClick={() => {
                    handleOpenChange(false);
                    onInviteFirstAdmin();
                  }}
                >
                  <UserPlus className="h-3.5 w-3.5" />
                  {t("iam.newOrgDialog.inviteFirstAdmin", "Invite first admin")}
                </Button>
              )}
            </div>
          </div>
        )}
        <OrgDrawerUnsavedChangesGuard
          open={unsavedCloseGuardOpen}
          translationScope="iam.newOrgDialog.unsavedGuard"
          onKeepEditing={() => setUnsavedCloseGuardOpen(false)}
          onDiscard={closeDrawer}
        />
      </div>
    </IamOverlayDrawer>
  );
}
